/**
 * Matrix View — the Effort–Value grid (S4f-A, on the 2B placement contract).
 *
 * PLACEMENT is decided by `actionPlanMatrixPlacement.ts` and is unchanged from 2B:
 * three destinations (grid / Value unavailable / Needs placement), the canonical
 * four-week comparative boundary, a discriminated value split with no fabricated
 * fallback, and the finite-number boundary. What S4f-A rebuilds is the DISPLAY
 * CONTRACT, per the approved gate prototype (rev 2, bcfce2f):
 *
 *  - the approved 3×2 grid: effort columns are the exhaustive predicates
 *    `<2 / 2–<4 / ≥4` weeks (never band ranges), refining — never contradicting —
 *    the shipped `<4 / ≥4` semantics; cell vocabulary Do first · Schedule next ·
 *    Plan carefully / When capacity allows · Fill-in work · Defer;
 *  - D8 "+N more": per-cell progressive disclosure with real expand/collapse
 *    semantics, stable ordering, and every match revealed while filters are
 *    active — a matching task is never silently hidden;
 *  - a keyboard grid/list contract: ONE tab stop with a roving tabindex, arrow
 *    keys traverse every rendered card in reading order, Enter/Space opens the
 *    drawer (native button), Home/End jump — every task provably reachable (D10);
 *  - namespaced selectors: everything here is `.ap-*`. The global RACI
 *    `.matrix-header` / `.matrix-row` family (RACIBuilderModal.css) can no longer
 *    touch this surface, in either direction — regression-tested by asserting no
 *    rendered element carries a `matrix-*` class.
 *
 * Filters stay visibility-only (panel-owned state): the partition, the split and
 * every cell assignment derive from the full task set before any filter runs.
 */

import React, { useMemo, useRef, useState } from 'react';
import {
  AP_TASK_DRAWER_ID,
  EFFORT_UNAVAILABLE_LABEL,
  UNAVAILABLE_LABEL,
  formatCurrency,
  formatEffortWeeks,
  getStatusLabel,
} from './actionPlanValueFormat';
import { PROVENANCE_LABEL } from '../types/actionPlanValueProvenance';
import type { ValueProvenance } from '../types/actionPlanValueProvenance';
import {
  EFFORT_BAND_WEEKS,
  MATRIX_CELL_LABELS,
  MATRIX_EFFORT_BOUNDARY_WEEKS,
  MATRIX_EFFORT_COLUMNS,
  anyFilterActive,
  countByProvenance,
  deriveValueSplit,
  effortColumnOf,
  hasFiniteValue,
  matchesFilters,
  partitionMatrixTasks,
  stableTaskOrder,
  valueRowOf,
} from './actionPlanMatrixPlacement';
import type {
  MatrixEffortColumn,
  MatrixFilters,
  MatrixValueRow,
} from './actionPlanMatrixPlacement';
import type {
  Phase,
  Task,
  TaskPriority,
  TaskStatus,
  TaskStatusPersisted,
} from '../types/actionPlan';

// ============================================================================
// COMPONENT PROPS
// ============================================================================

interface MatrixViewProps {
  tasks: Task[];
  /** For the Phase filter's option list (S4f-B). */
  phases: Phase[];
  onTaskClick: (taskId: string) => void;
  onTaskStatusChange: (taskId: string, status: TaskStatus) => void;
  /** Canonical selection (URL state, panel-owned) — survives view switches. */
  selectedTaskId?: string | null;
  /** The plan-wide filter object, panel-owned (S4e/S4f-B); Matrix consumes, never owns. */
  filters: MatrixFilters;
  onFiltersChange: (filters: MatrixFilters) => void;
  onClearFilters: () => void;
}

// ============================================================================
// STATUS / PRIORITY PRESENTATION
// ============================================================================

/**
 * `blocked` and `skipped` are PERSISTED states (migration 021:137, retained by
 * 141), not settable ones. They must render as themselves. FAIL CLOSED on
 * anything else: a malformed wire status gets the non-state glyph, never the
 * paused one (1578c38).
 */
const getStatusIcon = (status: TaskStatusPersisted): string => {
  switch (status) {
    case 'completed': return '✅';
    case 'in_progress': return '🔄';
    case 'not_started': return '⏸️';
    case 'blocked': return '⛔';
    case 'skipped': return '⏭️';
    default: return '❓';
  }
};

const getPriorityColor = (priority: string): string => {
  switch (priority) {
    case 'critical': return '#dc2626';
    case 'high': return '#f59e0b';
    case 'medium': return '#3b82f6';
    case 'low': return '#9ca3af';
    default: return '#9ca3af';
  }
};

const PRIORITY_LABEL: Record<TaskPriority, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const STATUS_OPTIONS: TaskStatusPersisted[] = [
  'not_started',
  'in_progress',
  'completed',
  'blocked',
  'skipped',
];
const PRIORITY_OPTIONS: TaskPriority[] = ['critical', 'high', 'medium', 'low'];
const PROVENANCE_OPTIONS: ValueProvenance[] = [
  'observed',
  'user_assumption',
  'reference_assumption',
  'model_estimate',
  'legacy_unverified',
  'unavailable',
];

/** D8: how many tasks a cell shows before "+N more", when no filter is active. */
export const MATRIX_CELL_DEFAULT_VISIBLE = 4;

// ============================================================================
// TASK CARD — one implementation for every region
// ============================================================================

interface MatrixTaskCardProps {
  task: Task;
  selected: boolean;
  onTaskClick: (taskId: string) => void;
  variant: 'grid' | 'value-unavailable' | 'needs-placement';
  /** Roving tabindex (S4f-A): exactly one card is the tab stop. */
  tabStop: boolean;
  onCardFocus: (taskId: string) => void;
}

/**
 * A real `<button>`, so Enter and Space operate it natively. The accessible name
 * carries title, status, effort, value and priority so none of them relies on
 * hover, colour, or the emoji.
 */
const MatrixTaskCard: React.FC<MatrixTaskCardProps> = ({
  task,
  selected,
  onTaskClick,
  variant,
  tabStop,
  onCardFocus,
}) => {
  // Finite gate, not just the provenance gate: a NaN or Infinity claiming
  // `observed` provenance must read Unavailable AND be classified unavailable.
  const valueText = hasFiniteValue(task.value_usd)
    ? formatCurrency(task.value_usd.value)
    : UNAVAILABLE_LABEL;
  const effortText = formatEffortWeeks(task.effort_weeks);
  const accessibleName =
    `${task.title} — ${getStatusLabel(task.status)}, ` +
    `${effortText === EFFORT_UNAVAILABLE_LABEL ? 'effort unavailable' : `effort ${effortText}`}, ` +
    `${valueText === UNAVAILABLE_LABEL ? 'value unavailable' : `value ${valueText}`}, ` +
    `priority ${PRIORITY_LABEL[task.priority] ?? task.priority}` +
    `${task.is_critical_path ? ', on the critical path' : ''}`;

  return (
    <button
      type="button"
      data-ap-task-button={task.id}
      className={`ap-task-card ap-task-card--st-${task.status}${variant !== 'grid' ? ' ap-task-card--offgrid' : ''}${task.is_critical_path ? ' ap-task-card--critical' : ''}${selected ? ' is-selected' : ''}`}
      aria-current={selected ? 'true' : undefined}
      aria-expanded={selected}
      aria-controls={AP_TASK_DRAWER_ID}
      aria-label={accessibleName}
      tabIndex={tabStop ? 0 : -1}
      onFocus={() => onCardFocus(task.id)}
      onClick={() => onTaskClick(task.id)}
    >
      <span className="ap-task-card__head">
        <span className="ap-task-card__status" role="img" aria-hidden="true">{getStatusIcon(task.status)}</span>
        {task.is_critical_path && <span className="ap-task-card__star" aria-hidden="true">⭐</span>}
        <span
          className="ap-task-card__priority"
          style={{ backgroundColor: getPriorityColor(task.priority) }}
          aria-hidden="true"
        ></span>
      </span>
      <span className="ap-task-card__title">{task.title}</span>
      <span className="ap-task-card__meta">
        <span className={effortText === EFFORT_UNAVAILABLE_LABEL ? 'ap-task-card__effort ap-task-card__effort--unavailable' : 'ap-task-card__effort'}>
          {effortText}
        </span>
        <span className={valueText === UNAVAILABLE_LABEL ? 'ap-task-card__value ap-task-card__value--unavailable' : 'ap-task-card__value'}>
          {valueText}
        </span>
      </span>
      {variant === 'value-unavailable' && task.value_usd?.basis && (
        <span className="ap-task-card__basis">{task.value_usd.basis}</span>
      )}
    </button>
  );
};

// ============================================================================
// CELL DISCLOSURE — D8 "+N more"
// ============================================================================

interface CellTasksProps {
  /** DOM id for aria-controls. */
  cellDomId: string;
  /** Already filtered to the VISIBLE set, in stable order. */
  visibleTasks: Task[];
  /** Total placed in this cell before filtering — for the honest count badge. */
  filtersActive: boolean;
  emptyText: string;
  renderCard: (task: Task, variant: MatrixTaskCardProps['variant']) => React.ReactNode;
  variant: MatrixTaskCardProps['variant'];
}

/**
 * Per-cell progressive disclosure (D8, binding): a real expand/collapse control
 * with `aria-expanded` + `aria-controls`; "Show fewer" when expanded; stable
 * ordering (the caller sorts); focus stays on the control across toggles (it is
 * the same button); and WHEN FILTERS OR SEARCH ARE ACTIVE EVERY MATCH IS SHOWN —
 * a matching task is never hidden behind "+N more". No pagination, no
 * virtualization.
 */
const CellTasks: React.FC<CellTasksProps> = ({
  cellDomId,
  visibleTasks,
  filtersActive,
  emptyText,
  renderCard,
  variant,
}) => {
  const [expanded, setExpanded] = useState(false);
  const showAll = expanded || filtersActive;
  const shown = showAll ? visibleTasks : visibleTasks.slice(0, MATRIX_CELL_DEFAULT_VISIBLE);
  const hiddenCount = visibleTasks.length - shown.length;
  const collapsible = !filtersActive && visibleTasks.length > MATRIX_CELL_DEFAULT_VISIBLE;

  return (
    <>
      <div id={cellDomId} className="ap-matrix-cell__tasks">
        {shown.map((task) => renderCard(task, variant))}
        {visibleTasks.length === 0 && <div className="ap-matrix-cell__empty">{emptyText}</div>}
      </div>
      {collapsible && (
        <button
          type="button"
          className="ap-matrix-cell__more"
          aria-expanded={expanded}
          aria-controls={cellDomId}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show fewer' : `+${hiddenCount} more`}
        </button>
      )}
    </>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const MatrixView: React.FC<MatrixViewProps> = ({
  tasks,
  phases,
  onTaskClick,
  onTaskStatusChange: _onTaskStatusChange,
  selectedTaskId = null,
  filters,
  onFiltersChange,
  onClearFilters,
}) => {
  /*
   * DESTINATIONS AND THRESHOLDS COME FROM THE FULL SET (§6D items 3 and 5).
   * Filters only decide which of the already-placed cards render.
   */
  const { grid, valueUnavailable, needsPlacement } = useMemo(
    () => partitionMatrixTasks(tasks),
    [tasks],
  );
  const split = useMemo(() => deriveValueSplit(grid), [grid]);
  const provenanceCounts = useMemo(() => countByProvenance(tasks), [tasks]);

  const isVisible = (t: Task): boolean => matchesFilters(t, filters);
  const visibleTotal = useMemo(
    () => tasks.filter((t) => matchesFilters(t, filters)).length,
    [tasks, filters],
  );
  const filtersActive = anyFilterActive(filters);

  /** Region badge: honest about visibility without re-placing anything. */
  const regionCount = (all: readonly Task[], visible: number): string =>
    filtersActive ? `${visible} of ${all.length}` : `${all.length}`;

  // --------------------------------------------------------------------------
  // The 3×2 display geometry (D1 as approved): cells derived once, stably sorted.
  // --------------------------------------------------------------------------

  const cells = useMemo(() => {
    const byCell = new Map<string, Task[]>();
    if (split.kind === 'split') {
      for (const task of grid) {
        const key = `${valueRowOf(task, split)}-${effortColumnOf(task.effort_weeks)}`;
        byCell.set(key, [...(byCell.get(key) ?? []), task]);
      }
    }
    for (const list of byCell.values()) list.sort(stableTaskOrder);
    return byCell;
  }, [grid, split]);

  const unrankedByColumn = useMemo(() => {
    const byCol = new Map<MatrixEffortColumn, Task[]>();
    if (split.kind === 'single' || split.kind === 'uniform') {
      for (const task of grid) {
        const col = effortColumnOf(task.effort_weeks as number);
        byCol.set(col, [...(byCol.get(col) ?? []), task]);
      }
    }
    for (const list of byCol.values()) list.sort(stableTaskOrder);
    return byCol;
  }, [grid, split]);

  const valueUnavailableByColumn = useMemo(() => {
    const byCol = new Map<MatrixEffortColumn, Task[]>();
    for (const task of valueUnavailable) {
      const col = effortColumnOf(task.effort_weeks);
      byCol.set(col, [...(byCol.get(col) ?? []), task]);
    }
    for (const list of byCol.values()) list.sort(stableTaskOrder);
    return byCol;
  }, [valueUnavailable]);

  const needsPlacementSorted = useMemo(
    () => [...needsPlacement].sort(stableTaskOrder),
    [needsPlacement],
  );

  // --------------------------------------------------------------------------
  // Keyboard contract (S4f-A / D10): one tab stop, arrows traverse every card.
  // --------------------------------------------------------------------------

  const rootRef = useRef<HTMLDivElement | null>(null);
  /**
   * The roving tab stop. Defaults to the first rendered card; follows focus so
   * leaving and re-entering the Matrix returns to the last-visited task.
   */
  const [rovingId, setRovingId] = useState<string | null>(null);

  /** All rendered cards in reading order — the DOM is the order authority. */
  const orderedCardIds = (): string[] => {
    const root = rootRef.current;
    if (!root) return [];
    return Array.from(root.querySelectorAll<HTMLElement>('[data-ap-task-button]')).map(
      (el) => el.dataset.apTaskButton as string,
    );
  };

  const handleGridKeys = (event: React.KeyboardEvent) => {
    const target = event.target as HTMLElement;
    const currentId = target.dataset?.apTaskButton;
    if (!currentId) return;
    const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;

    const ids = orderedCardIds();
    const index = ids.indexOf(currentId);
    if (index < 0) return;
    let next = index;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = Math.min(ids.length - 1, index + 1);
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = Math.max(0, index - 1);
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = ids.length - 1;
    if (next === index) {
      event.preventDefault();
      return;
    }
    event.preventDefault();
    const root = rootRef.current;
    const el = root?.querySelector<HTMLElement>(`[data-ap-task-button="${CSS.escape(ids[next])}"]`);
    if (el) {
      setRovingId(ids[next]);
      el.focus();
    }
  };

  const firstRenderedId = useMemo(() => {
    // The first card in reading order gets the tab stop until focus moves.
    // Recomputed from the same ordered structures the render uses.
    const firstCellTask = (): string | null => {
      if (split.kind === 'split') {
        for (const row of ['above', 'below'] as MatrixValueRow[]) {
          for (const col of MATRIX_EFFORT_COLUMNS) {
            const list = (cells.get(`${row}-${col.id}`) ?? []).filter(isVisible);
            if (list.length > 0) return list[0].id;
          }
        }
      }
      for (const col of MATRIX_EFFORT_COLUMNS) {
        const list = (unrankedByColumn.get(col.id) ?? []).filter(isVisible);
        if (list.length > 0) return list[0].id;
      }
      for (const col of MATRIX_EFFORT_COLUMNS) {
        const list = (valueUnavailableByColumn.get(col.id) ?? []).filter(isVisible);
        if (list.length > 0) return list[0].id;
      }
      const np = needsPlacementSorted.filter(isVisible);
      return np.length > 0 ? np[0].id : null;
    };
    return firstCellTask();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isVisible is derived from `filters`
  }, [cells, unrankedByColumn, valueUnavailableByColumn, needsPlacementSorted, split, filters]);

  const tabStopId = rovingId ?? firstRenderedId;

  const renderCard = (task: Task, variant: MatrixTaskCardProps['variant']) => (
    <MatrixTaskCard
      key={task.id}
      task={task}
      selected={selectedTaskId === task.id}
      onTaskClick={onTaskClick}
      variant={variant}
      tabStop={tabStopId === task.id}
      onCardFocus={setRovingId}
    />
  );

  const setFilter = <K extends keyof MatrixFilters>(key: K, value: MatrixFilters[K]) =>
    onFiltersChange({ ...filters, [key]: value });

  const thresholdLabel = split.kind === 'split' ? formatCurrency(split.threshold) : null;

  return (
    <div className="ap-matrix" ref={rootRef} onKeyDown={handleGridKeys}>
      {/* Head */}
      <div className="ap-matrix-head">
        <h3>Effort-Value Matrix</h3>
        <p className="ap-matrix-head__subtitle">
          Tasks categorized by business value vs implementation effort
        </p>
      </div>

      {/* Toolbar — visibility only; nothing here feeds placement. */}
      <div className="ap-matrix-toolbar" role="group" aria-label="Filter tasks">
        <div className="ap-matrix-toolbar__field">
          <label htmlFor="ap-matrix-search">Search</label>
          <input
            id="ap-matrix-search"
            type="search"
            value={filters.search}
            placeholder="Task title"
            onChange={(e) => setFilter('search', e.target.value)}
          />
        </div>
        <div className="ap-matrix-toolbar__field">
          <label htmlFor="ap-matrix-status">Status</label>
          <select
            id="ap-matrix-status"
            value={filters.status}
            onChange={(e) => setFilter('status', e.target.value as MatrixFilters['status'])}
          >
            <option value="all">Any status</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{getStatusLabel(s)}</option>
            ))}
          </select>
        </div>
        <div className="ap-matrix-toolbar__field">
          <label htmlFor="ap-matrix-priority">Priority</label>
          <select
            id="ap-matrix-priority"
            value={filters.priority}
            onChange={(e) => setFilter('priority', e.target.value as MatrixFilters['priority'])}
          >
            <option value="all">Any priority</option>
            {PRIORITY_OPTIONS.map((p) => (
              <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>
            ))}
          </select>
        </div>
        <div className="ap-matrix-toolbar__field">
          <label htmlFor="ap-matrix-phase">Phase</label>
          <select
            id="ap-matrix-phase"
            value={filters.phase}
            onChange={(e) => setFilter('phase', e.target.value)}
          >
            <option value="all">All phases</option>
            {phases.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="ap-matrix-toolbar__field">
          <label htmlFor="ap-matrix-confidence">Confidence</label>
          {/* A task with no recorded confidence matches only "Any" — filtering by
              High must not surface tasks whose confidence was never assessed. */}
          <select
            id="ap-matrix-confidence"
            value={filters.confidence}
            onChange={(e) =>
              setFilter('confidence', e.target.value as MatrixFilters['confidence'])
            }
          >
            <option value="all">Any confidence</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
        </div>
        <div className="ap-matrix-toolbar__field">
          <label htmlFor="ap-matrix-value-source">Value source</label>
          <select
            id="ap-matrix-value-source"
            value={filters.valueSource}
            onChange={(e) =>
              setFilter('valueSource', e.target.value as MatrixFilters['valueSource'])
            }
          >
            <option value="all">Any source</option>
            {PROVENANCE_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {`${PROVENANCE_LABEL[p]} (${provenanceCounts[p]})`}
              </option>
            ))}
          </select>
        </div>
        <div className="ap-matrix-toolbar__meta">
          <span className="ap-matrix-toolbar__count" data-testid="ap-matrix-result-count">
            Showing {visibleTotal} of {tasks.length} tasks
          </span>
          {filtersActive && (
            <button type="button" className="ap-matrix-toolbar__clear" onClick={onClearFilters}>
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* ----------------------------------------------------------------------
        The grid destination — the approved 3×2 (D1): exhaustive effort predicates
        as columns, the real median as rows.
      ---------------------------------------------------------------------- */}
      {split.kind === 'split' && (
        <div className="ap-matrix-scroll" role="group" aria-label="Value against effort — tasks with both coordinates">
          <div className="ap-matrix-grid">
            <div className="ap-matrix-axis">Value ↑<br /><small>vs effort →</small></div>
            {MATRIX_EFFORT_COLUMNS.map((col) => (
              <div key={col.id} className="ap-matrix-axis">
                {col.label}<br /><small>{col.predicate}</small>
              </div>
            ))}

            {(['above', 'below'] as MatrixValueRow[]).map((row) => (
              <React.Fragment key={row}>
                <div className="ap-matrix-axis">
                  {row === 'above' ? 'At or above' : 'Below'}<br />{thresholdLabel}
                </div>
                {MATRIX_EFFORT_COLUMNS.map((col) => {
                  const all = cells.get(`${row}-${col.id}`) ?? [];
                  const visible = all.filter(isVisible);
                  const domId = `ap-matrix-cell-${row}-${col.id}`;
                  return (
                    <div key={col.id} className="ap-matrix-cell">
                      <h4 className="ap-matrix-cell__header">
                        {MATRIX_CELL_LABELS[row][col.id]}{' '}
                        <span>{regionCount(all, visible.length)}</span>
                      </h4>
                      <CellTasks
                        cellDomId={domId}
                        visibleTasks={visible}
                        filtersActive={filtersActive}
                        emptyText={all.length === 0 ? 'No tasks' : 'No tasks match the filters'}
                        renderCard={renderCard}
                        variant="grid"
                      />
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {(split.kind === 'single' || split.kind === 'uniform') && (
        <section
          className="ap-matrix-unranked"
          aria-label="Priced tasks — not ranked by value"
          data-testid="ap-matrix-unranked"
        >
          <div className="ap-matrix-unranked__header">
            <h4>{split.kind === 'single' ? 'Only priced task' : 'At the plan median'}</h4>
            <span className="ap-matrix-band__count">
              {regionCount(grid, grid.filter(isVisible).length)}
            </span>
          </div>
          <p className="ap-matrix-unranked__description">
            {split.kind === 'single'
              ? 'One priced task cannot be ranked against the rest of the plan, so it is placed by effort only — no high-or-low value claim is made.'
              : 'Every priced task in this plan has the same value, so none of them is higher or lower than another. They are placed by effort only.'}
          </p>
          <div className="ap-matrix-band-cols">
            {MATRIX_EFFORT_COLUMNS.map((col) => {
              const all = unrankedByColumn.get(col.id) ?? [];
              const visible = all.filter(isVisible);
              return (
                <div key={col.id} className="ap-matrix-band-cols__cell">
                  <h5>{col.label} effort <small>({col.predicate})</small></h5>
                  <CellTasks
                    cellDomId={`ap-matrix-unranked-${col.id}`}
                    visibleTasks={visible}
                    filtersActive={filtersActive}
                    emptyText={all.length === 0 ? 'No tasks' : 'No tasks match the filters'}
                    renderCard={renderCard}
                    variant="grid"
                  />
                </div>
              );
            })}
          </div>
        </section>
      )}

      {split.kind === 'none' && (
        <div className="ap-matrix-empty" data-testid="ap-matrix-empty-grid">
          <h4>No tasks can be plotted</h4>
          <p>
            The grid needs at least one task with both an effort estimate and a usable
            value. {tasks.length === 0
              ? 'This plan has no tasks.'
              : 'The tasks in this plan are listed below with the input each one is missing.'}
          </p>
        </div>
      )}

      {/* ----------------------------------------------------------------------
        Threshold explainer — BELOW the grid (S4e fold frame). Never $100,000.
      ---------------------------------------------------------------------- */}
      <p className="ap-matrix-thresholds" data-testid="ap-matrix-thresholds">
        <strong>How the grid is divided.</strong>{' '}
        <b>Effort</b> columns are exhaustive placement rules — low &lt;2 wk, medium 2–&lt;4 wk,
        high ≥{MATRIX_EFFORT_BOUNDARY_WEEKS} wk — derived from the plan generator&apos;s effort
        model (bands {EFFORT_BAND_WEEKS.low.min}–{EFFORT_BAND_WEEKS.low.max} /{' '}
        {EFFORT_BAND_WEEKS.medium.min}–{EFFORT_BAND_WEEKS.medium.max} /{' '}
        {EFFORT_BAND_WEEKS.high.min}–{EFFORT_BAND_WEEKS.high.max} wk), not a statistic of this
        plan; low and medium together are the under-{MATRIX_EFFORT_BOUNDARY_WEEKS}-week side of
        the canonical boundary.{' '}
        {split.kind === 'split' && (
          <>
            <b>Value</b> splits at the median priced task value in this plan,{' '}
            <b>{formatCurrency(split.threshold)}</b> ({grid.length} priced task
            {grid.length === 1 ? '' : 's'}; {split.atOrAboveCount} at or above,{' '}
            {split.belowCount} below). The median is used rather than a fixed dollar figure
            so the split describes this plan rather than an imagined average one.
          </>
        )}
        {split.kind === 'single' && (
          <>
            <b>Value</b> is not divided: only one task is priced
            ({formatCurrency(split.value)}), and a single value has nothing to be compared
            against — it is shown without a high-or-low claim.
          </>
        )}
        {split.kind === 'uniform' && (
          <>
            <b>Value</b> is not divided: all {grid.length} priced tasks share the same value
            ({formatCurrency(split.value)}), so there is no line to draw between them.
          </>
        )}
        {split.kind === 'none' && (
          <>
            <b>Value</b> threshold is unavailable: no task in this plan has a usable value,
            so there is nothing to divide.
          </>
        )}
      </p>

      {/* Zero-result recovery: never an empty surface with no way back. */}
      {filtersActive && visibleTotal === 0 && tasks.length > 0 && (
        <div className="ap-matrix-zero" role="status">
          <p>No tasks match the current filters.</p>
          <button type="button" className="ap-matrix-toolbar__clear" onClick={onClearFilters}>
            Clear filters
          </button>
        </div>
      )}

      {/* ----------------------------------------------------------------------
        Value unavailable — effort known, value not. Under the REAL effort columns.
      ---------------------------------------------------------------------- */}
      {valueUnavailable.length > 0 && (
        <section className="ap-matrix-band" aria-label="Tasks with unavailable value">
          <div className="ap-matrix-band__header">
            <h4>Value unavailable</h4>
            <span className="ap-matrix-band__count">
              {regionCount(valueUnavailable, valueUnavailable.filter(isVisible).length)}
            </span>
          </div>
          <p className="ap-matrix-band__description">
            These tasks are not plotted because their business value is not known — which
            is different from being low. Their effort is known, so they are grouped under
            their real effort column. They are still part of the plan.
          </p>
          <div className="ap-matrix-band-cols">
            {MATRIX_EFFORT_COLUMNS.map((col) => {
              const all = valueUnavailableByColumn.get(col.id) ?? [];
              const visible = all.filter(isVisible);
              return (
                <div key={col.id} className="ap-matrix-band-cols__cell">
                  <h5>{col.label} effort <small>({col.predicate})</small></h5>
                  <CellTasks
                    cellDomId={`ap-matrix-vu-${col.id}`}
                    visibleTasks={visible}
                    filtersActive={filtersActive}
                    emptyText={all.length === 0 ? 'No tasks' : 'No tasks match the filters'}
                    renderCard={renderCard}
                    variant="value-unavailable"
                  />
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ----------------------------------------------------------------------
        Needs placement — no effort estimate, so no coordinate on either axis.
      ---------------------------------------------------------------------- */}
      {needsPlacement.length > 0 && (
        <section
          className="ap-matrix-band ap-matrix-band--needs-placement"
          aria-label="Tasks that need placement"
        >
          <div className="ap-matrix-band__header">
            <h4>Needs placement</h4>
            <span className="ap-matrix-band__count">
              {regionCount(needsPlacement, needsPlacementSorted.filter(isVisible).length)}
            </span>
          </div>
          <p className="ap-matrix-band__description">
            No effort estimate is stored for these tasks — which is different from taking
            no time — so they cannot sit on either axis. Each card shows what is missing.
            They are still part of the plan.
          </p>
          <CellTasks
            cellDomId="ap-matrix-np"
            visibleTasks={needsPlacementSorted.filter(isVisible)}
            filtersActive={filtersActive}
            emptyText={filtersActive ? 'No tasks match the filters' : 'No tasks'}
            renderCard={renderCard}
            variant="needs-placement"
          />
        </section>
      )}
    </div>
  );
};
